import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    doc,
    updateDoc,
    increment
} from "firebase/firestore";
import { DatabaseReference, ref, runTransaction, get } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { startAuthentication } from "@simplewebauthn/browser";
import { fs, db, functions } from "../firebase.ts";

// deposits, withdraws, transfers, donations, rewards // TODO: later
export interface Transactions {
    id?: string;
    date?: Timestamp;
}

type Response = {
    text: string;
    color: "green" | "red" | "orange"; // status
    pool?: number;
};

export class FamilyBank {
    private pool: DatabaseReference; // aquarian pool // b-kitty
    private collection: CollectionReference<DocumentData>;

    constructor() {
        this.pool = ref(db, "pool");
        this.collection = collection(fs, "transactions");
    }

    async getPool(): Promise<number> {
        try {
            const snapshot = await get(this.pool);
            if (snapshot.exists()) {
                return snapshot.val() as number;
            } else {
                //console.log("No data available");
                return 0; // Return 0 if the counter doesn't exist yet
            }
        } catch (error) {
            console.error("Error fetching Aquarian B-Kitty:", error);
            throw error;
        }
    }

    async incrementPool(kiitti: number): Promise<number> {
        try {
            const result = await runTransaction(
                this.pool,
                (currentValue: number | null) => {
                    // If the data doesn't exist yet, initialize it to 1
                    if (currentValue === null) {
                        return kiitti;
                    }

                    // Otherwise, increment the current value
                    return currentValue + kiitti;
                }
            );

            if (result.committed) {
                console.log(`New Aquarian B-Kitty: ${result.snapshot.val()}`);
                return result.snapshot.val();
            } else {
                throw new Error("Transaction not committed.");
            }
        } catch (error) {
            console.error("Increment failed: ", error);
            throw error;
        }
    }

    async getReserve(): Promise<number> {
        let reserve = await this.getPool();
        const heroes = JSON.parse(localStorage.getItem("heroes") || "[]");
        for (const hero of heroes) reserve += hero.account + hero.fund;
        return reserve;
    }

    /// USER

    // not used
    async getAccount(uid: string) {}
    async getFund(uid: string) {}

    // transfer from b-account to b-fund
    async transfer(amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/transfer" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/transfer",
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // public send
    async send(to: string, amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/send" })).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/send",
                to,
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // anonymous donate to pool
    async donate(amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/donate" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            const pool = await verify({
                assertion,
                action: "bank/donate",
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green",
                pool: pool.data as number
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // anonymous donate to fund
    async fund(to: string, amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/fund" })).data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/fund",
                to,
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // anonymous tip to account: done after thanking

    /// BANKER

    // anonymous reward to fund
    async reward(to: string, amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/reward" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/reward",
                to,
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async deposit(
        to: string,
        amount: number,
        isAccount: boolean
    ): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/deposit" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/deposit",
                to,
                amount,
                isAccount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    async withdraw(to: string, amount: number): Promise<Response> {
        localStorage.setItem("amount", amount.toString());
        try {
            const getOpts = httpsCallable(functions, "getActionOptions");
            const options: any = (await getOpts({ action: "bank/withdraw" }))
                .data;
            const assertion = await startAuthentication({
                optionsJSON: options
            });

            const verify = httpsCallable(functions, "verifyAction");
            await verify({
                assertion,
                action: "bank/withdraw",
                to,
                amount
            });

            localStorage.removeItem("amount");
            return {
                text: "Success",
                color: "green"
            };
        } catch (error: any) {
            switch (error.name) {
                case "NotAllowedError":
                    if (error.message === "The document is not focused.") {
                        alert("Please try again one more time");
                        return {
                            text: "Page focus required. Please try again one more time.",
                            color: "orange"
                        };
                    } else {
                        localStorage.removeItem("amount");
                        return {
                            text: "Passkey verification was cancelled.",
                            color: "orange"
                        };
                    }
                default:
                    return { text: error.message, color: "red" };
            }
        }
    }

    // not used
    async deposit2(uid: string, amount: number) {
        const userDocRef = doc(fs, "users", uid);
        await updateDoc(userDocRef, { account: increment(amount) });
    }

    // not used
    async withdraw2(uid: string, amount: number) {
        const userDocRef = doc(fs, "users", uid);
        await updateDoc(userDocRef, { account: increment(-amount) });
    }
}
